// =============================================================================
// Le cadre ouvert (DEV-2284, lot B) : des fonctions PURES, sans Vue.
//
// - `dernierJourMesure` : D14. Le dernier jour qui porte une mesure, lu dans la
//   clé du moteur quand elle est remplie (DEV-2237, servie en JJ/MM/AAAA), sinon
//   dans la trame jour par jour ; jamais déduit de la fin de la période.
// - `piedDuCadre` : la relance dit ce qu'elle recalcule (maquette v0.4, pied du
//   cadre) : la liste des modifications, un seul bouton primaire libellé par
//   leur nombre ; depuis Q26 (14/10/2026), rien quand rien n'est modifié (la
//   relance sur les dernières mesures est « Nouvelle étude »).
// - `enteteReglagesAvances` : l'en-tête de rang 2 des
//   groupes du cadre (table « Les en-têtes », lignes du cadre).
// =============================================================================
import { lirePeriode } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import {
    lireInstant,
    dateDeCalcul,
} from "@modules/courtage/utils/optim-turpe-v2.cycle"
import { formatDateFr } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import type { Entete } from "@modules/courtage/utils/optim-turpe-v2.entetes"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

// -----------------------------------------------------------------------------
// D14 : le dernier jour mesuré
// -----------------------------------------------------------------------------

/**
 * Au-delà de cet âge (en jours, entre le dernier jour mesuré et le calcul), la
 * ligne du cadre écrit l'âge des mesures, en alerte. La maquette écrit la date
 * seule à cinq jours (24/09 pour un calcul du 29/09) et l'âge à vingt-neuf ;
 * « quelques jours » est fixé ici à une semaine.
 */
export const SEUIL_AGE_MESURES_JOURS = 7

export type DernierJourMesure = {
    /** Le jour, en ISO (AAAA-MM-JJ). */
    date: string
    /** Jours pleins entre ce jour et la date du calcul ; `null` sans date de calcul. */
    ageJours: number | null
    source: "moteur" | "trame"
}

/** « 31/08/2026 » ou « 2026-08-31… » vers « 2026-08-31 » ; `null` sinon. */
function versIso(valeur: unknown): string | null {
    if (typeof valeur !== "string") return null
    const brut = valeur.trim()
    const fr = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(brut)
    const iso = fr ? `${fr[3]}-${fr[2]}-${fr[1]}` : brut.slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
    return Number.isNaN(Date.parse(`${iso}T00:00:00Z`)) ? null : iso
}

function ageEnJours(iso: string, etude: OptimTurpeV2Etude): number | null {
    const instant = lireInstant(dateDeCalcul(etude))
    if (instant === null) return null
    const jourDuCalcul = Date.parse(
        `${new Date(instant).toISOString().slice(0, 10)}T00:00:00Z`
    )
    const jourMesure = Date.parse(`${iso}T00:00:00Z`)
    return Math.round((jourDuCalcul - jourMesure) / 86_400_000)
}

export function dernierJourMesure(
    etude: OptimTurpeV2Etude | null | undefined
): DernierJourMesure | null {
    if (!etude) return null
    const qualite = etude.qualite_donnees
    const servi = versIso(qualite?.derniere_donnee_detenue)
    if (servi) {
        return {
            date: servi,
            ageJours: ageEnJours(servi, etude),
            source: "moteur",
        }
    }
    const trame = Array.isArray(qualite?.trame_par_jour)
        ? qualite.trame_par_jour
        : []
    let dernier: string | null = null
    for (const jour of trame) {
        const mesures = jour?.pas_mesures
        if (typeof mesures !== "number" || !Number.isFinite(mesures)) continue
        if (mesures <= 0) continue
        const iso = versIso(jour.date)
        if (iso && (dernier === null || iso > dernier)) dernier = iso
    }
    if (!dernier) return null
    return {
        date: dernier,
        ageJours: ageEnJours(dernier, etude),
        source: "trame",
    }
}

/**
 * La réserve que le moteur pose quand il établit la collecte du compteur comme
 * interrompue avant la fin de la période (courtage, `reserves_donnees_v2`).
 */
export const CODE_RESERVE_COLLECTE_INTERROMPUE = "collecte-interrompue-datee"

/** Le lendemain d'un jour ISO, en ISO. */
function lendemain(iso: string): string {
    return new Date(Date.parse(`${iso}T00:00:00Z`) + 86_400_000)
        .toISOString()
        .slice(0, 10)
}

/**
 * La note des mesures : son texte ; `alerte` quand le dernier jour mesuré est
 * ancien sans arrêt établi ; `arretDepuis` (JJ/MM/AAAA) quand le moteur a
 * établi l'arrêt de la collecte (constat gris, décision (n) du 20/10/2026).
 */
export type NoteDernierJourMesure = {
    texte: string
    alerte: boolean
    arretDepuis: string | null
}

/**
 * Ce que la ligne du cadre écrit sous la période, ou `null`.
 *
 * Q20 et R18 (14/10/2026) : l'arrêt des mesures se dit dans la ligne des
 * hypothèses, daté au moment de l'étude (A2 : un âge figé à la date du calcul
 * se lisait comme un âge d'aujourd'hui).
 *
 * Seconde passe de la revue de nuit (15/10/2026, E2) : le moteur sert
 * `derniere_donnee_detenue` sur TOUTE étude depuis DEV-2237 (la date de
 * l'interruption quand la collecte est établie comme interrompue, sinon le
 * dernier jour mesuré de la courbe de la période), et la trame ne couvre que
 * la période. Sur une période passée en collecte active, l'une et l'autre
 * donnent la fin de la période, pas la fin de la collecte. D'où trois règles,
 * quelle que soit la source :
 * - l'arrêt ne s'affirme que si le moteur sert la réserve
 *   `collecte-interrompue-datee`, seul fait établi d'une interruption ;
 *   décision (n) du 20/10/2026 (contrat v1.6) : la réserve se lit AVANT la
 *   garde de fin de période, car sur la fenêtre par défaut la période finit
 *   au dernier jour détenu et l'arrêt se taisait ; c'est un constat, gris,
 *   jamais une alerte (R-T6, C9, Q20) ;
 * - hors de cette réserve, une date qui atteint la fin de la période ne
 *   s'écrit pas (rien à dire) ;
 * - l'écart se mesure jusqu'au lendemain de la fin de la période, borné au
 *   jour du calcul, jamais jusqu'au jour du calcul seul (une période passée
 *   paraîtrait sinon arrêtée depuis des mois).
 */
export function noteDernierJourMesure(
    etude: OptimTurpeV2Etude | null | undefined
): NoteDernierJourMesure | null {
    const lu = dernierJourMesure(etude)
    const date = lu ? formatDateFr(lu.date) : null
    if (!etude || !lu || !date) return null
    const reserve =
        lu.source === "moteur"
            ? (Array.isArray(etude.reserves) ? etude.reserves : []).find(
                  (r) => r?.code === CODE_RESERVE_COLLECTE_INTERROMPUE
              )
            : undefined
    if (reserve) {
        // Contrat v1.6 §6 5.2 : le libellé SERVI, le même que le PDF imprime
        // (il varie : fenêtre par défaut ou fin demandée) ; la phrase n'est
        // composée ici que si le moteur n'en sert aucune.
        const servi =
            typeof reserve.label === "string" ? reserve.label.trim() : ""
        return {
            texte:
                servi ||
                `Au moment de l'étude, plus aucune mesure depuis le ${date} : l'étude s'arrête à cette date.`,
            alerte: false,
            arretDepuis: date,
        }
    }
    const finDePeriode = versIso(lirePeriode(etude).dateTo)
    if (finDePeriode !== null && lu.date >= finDePeriode) return null
    let ecart = lu.ageJours
    if (finDePeriode !== null) {
        const borne = Date.parse(`${lendemain(finDePeriode)}T00:00:00Z`)
        const jour = Date.parse(`${lu.date}T00:00:00Z`)
        const jusquALaFin = Math.round((borne - jour) / 86_400_000)
        ecart = ecart === null ? jusquALaFin : Math.min(ecart, jusquALaFin)
    }
    return {
        texte: `Dernier jour mesuré : ${date}`,
        alerte: ecart !== null && ecart > SEUIL_AGE_MESURES_JOURS,
        arretDepuis: null,
    }
}

/** Les constats de la ligne « periode » qui se disent ailleurs (bouton C15). */
const CONSTATS_DE_PERIODE_DITS_AILLEURS = new Set([
    "meilleure-periode",
    "periode-disponible",
])

/**
 * Les constats servis sur une ligne des hypothèses autre que « Mesures »
 * (moteur v1.6, contrat v1.6 §6 5.2) : « periode » (décision (s), la baisse
 * de puissance qui ouvre la période) et « contrat » (décision (t), une
 * souscrite servie à 0). Le libellé SERVI, celui que le PDF imprime au même
 * endroit, écrit après « · » TEL QUEL (contrat v1.6 §7.6). Un
 * constat gris, jamais une action ; aucun texte inventé.
 */
export function constatsDeLaLigne(
    etude: OptimTurpeV2Etude | null | undefined,
    ligne: "periode" | "contrat"
): string[] {
    const textes: string[] = []
    for (const r of Array.isArray(etude?.reserves) ? etude.reserves : []) {
        if (r?.ligne !== ligne || r.nature === "action") continue
        if (CONSTATS_DE_PERIODE_DITS_AILLEURS.has(r.code)) continue
        const brut = typeof r.label === "string" ? r.label.trim() : ""
        if (!brut) continue
        // Contrat v1.6 §7.6 : la même phrase sur la page et dans le PDF, donc
        // le libellé servi tel quel (ni minuscule initiale, ni point retiré).
        if (!textes.includes(brut)) textes.push(brut)
    }
    return textes
}

// -----------------------------------------------------------------------------
// Le pied du cadre
// -----------------------------------------------------------------------------

export type PiedDuCadre = {
    /** « Aucune modification. » ou « 2 modifications : prudence (5 %), segment (C2). » */
    liste: string
    /** Le libellé du bouton primaire, ou `null` quand il n'y a rien à relancer. */
    relancer: string | null
    /** Toujours faux depuis Q26 (14/10/2026) : « Actualiser » a quitté le pied. */
    actualiser: boolean
    /** « Annuler les modifications ». */
    annuler: boolean
    /** La phrase des conséquences, avant le bouton. */
    suite: string | null
    /** Ce que la ligne du cadre dit d'un brouillon non relancé. */
    attente: { etiquette: string; note: string } | null
}

export const SUITE_DE_LA_RELANCE =
    "Une nouvelle étude sera calculée : le résultat, le rapport et le texte de la demande au fournisseur suivront. L'étude actuelle reste dans « Études de ce compteur »."

/**
 * Q0 (revue du 08/10/2026) : avant le premier lancement, il n'y a pas d'étude
 * à remplacer ; la suite le dit.
 */
export const SUITE_DU_PREMIER_LANCEMENT =
    "Une première étude sera calculée : le résultat, le rapport et le texte de la demande au fournisseur suivront."

export function piedDuCadre(entree: {
    modifications: string[]
    /**
     * Revue 2 en production (C4, I82) : les mêmes modifications en forme
     * courte (« objectif », « puissances (HPH 60, HCB 64 kVA) »), pour la
     * ligne unique du pied collant ; la note d'attente garde la forme longue.
     */
    modificationsCourtes?: string[]
    actualisable: boolean
    /**
     * Q0 (revue du 08/10/2026) : aucune étude encore, le formulaire s'ouvre
     * avant le premier lancement. Sans modification, le pied lance l'étude
     * recommandée ; avec, il la lance avec les modifications. Rien n'attend
     * sur la ligne du cadre : il n'y a pas d'étude affichée à dépasser.
     */
    premierLancement?: boolean
    /**
     * Un refus, ou une étude rouverte en échec : « Relancer l'étude » reste
     * offert sans modification (relecture du lot B). Un refus d'origine
     * passagère (délai, service indisponible) se relance tel quel ; « Rejouer »
     * a quitté l'historique et ces états n'ont pas d'« Actualiser ».
     */
    relancable?: boolean
}): PiedDuCadre {
    const n = entree.modifications.length
    const s = n > 1 ? "s" : ""
    const liste = entree.modifications.join(", ")
    const courtes =
        entree.modificationsCourtes?.length === n
            ? entree.modificationsCourtes.join(", ")
            : liste
    if (entree.premierLancement === true) {
        return {
            liste:
                n === 0
                    ? "Aucune modification : les hypothèses par défaut."
                    : `${n} modification${s} : ${courtes}.`,
            relancer:
                n === 0
                    ? "Lancer l'étude"
                    : `Lancer l'étude avec ${n} modification${s}`,
            actualiser: false,
            annuler: n > 0,
            suite: SUITE_DU_PREMIER_LANCEMENT,
            attente: null,
        }
    }
    if (n === 0) {
        const relancable = entree.relancable === true
        // Q26 (14/10/2026) : « Actualiser » disparaît du pied ; la relance
        // sur les dernières mesures est le bouton « Nouvelle étude » du
        // résultat.
        return {
            liste: "Aucune modification.",
            relancer: relancable ? "Relancer l'étude" : null,
            actualiser: false,
            annuler: false,
            suite: relancable ? SUITE_DE_LA_RELANCE : null,
            attente: null,
        }
    }
    return {
        liste: `${n} modification${s} : ${courtes}.`,
        relancer: `Relancer l'étude avec ${n} modification${s}`,
        actualiser: false,
        annuler: true,
        suite: SUITE_DE_LA_RELANCE,
        attente: {
            etiquette: `${n} modification${s}, non relancée${s}`,
            // Série UX du témoin (15/10/2026, 2.3 point 8) : la note se lit
            // en phrase complète, plus en liste nue.
            note: `${n > 1 ? "Ces modifications ne sont" : "Cette modification n'est"} pas encore chiffrée${s} : ${liste}. Les sections ci-dessous restent celles de l'étude affichée.`,
        },
    }
}

// -----------------------------------------------------------------------------
// Les en-têtes des groupes du cadre (rang 2)
// -----------------------------------------------------------------------------

function repliee(
    libelle: string,
    noyau: string | null,
    complement: string | null = null
): Entete {
    return {
        forme: "repliee",
        libelle,
        noyau: noyau || null,
        complement: complement || null,
        pastille: null,
        pastilleReserve: false,
    }
}

/**
 * « Réglages d'expert (facultatif) · aucun réglage posé » (contrat v1.2
 * §8.2.11 et §8.2.12, maquette 2d, lot 4d). `poses` : les réglages saisis
 * dans le cadre, nommés. `palier` : en C5, le palier appliqué et sa
 * provenance (« palier de 1 en 1 kVA · valeur par défaut, compteur Linky
 * monophasé »), qui se lit sans clic. Le raccordement vit dans « Contrat
 * actuel », plus ici ; la grille TURPE se lit dans les hypothèses (Q41).
 */
export function enteteReglagesAvances(
    poses: string[],
    palier: string | null = null
): Entete {
    const n = poses.length
    const reglages =
        n === 0 ? null : `${n} réglage${n > 1 ? "s" : ""} : ${poses.join(", ")}`
    const noyau = palier
        ? [`palier ${palier}`, reglages].filter(Boolean).join(" · ")
        : (reglages ?? "aucun réglage posé")
    return repliee("Réglages d'expert (facultatif)", noyau)
}
